import { useCallback, useState } from 'react';
import { PointerSensor, TouchSensor, pointerWithin, useSensor, useSensors } from '@dnd-kit/core';
import type { DragStartEvent, DragEndEvent } from '@dnd-kit/core';

import {
  DROPPABLE_FOLDER_PREFIX,
  DROPPABLE_BREADCRUMB_PREFIX,
  DROPPABLE_BREADCRUMB_ROOT,
  DROPPABLE_BREADCRUMB_SIXROOT,
} from '@/components/organism/SixDetail/dnd/dndTypes';
import type { DragItemData } from '@/components/organism/SixDetail/dnd/dndTypes';
import type { FileEntry } from '@/types/SixDetail.types';

function isDragItemData(value: unknown): value is DragItemData {
  return (
    typeof value === 'object' &&
    value !== null &&
    'itemId' in value &&
    'selectedIds' in value
  );
}

const TOUCH_ACTIVATION_DELAY_MS = 250;
const TOUCH_ACTIVATION_TOLERANCE_PX = 5;
const POINTER_ACTIVATION_DISTANCE_PX = 5;

function resolveTargetPath(droppableId: string): string | undefined {
  if (droppableId === DROPPABLE_BREADCRUMB_ROOT || droppableId === DROPPABLE_BREADCRUMB_SIXROOT) {
    return undefined;
  }

  const breadcrumbIndex = droppableId.indexOf(DROPPABLE_BREADCRUMB_PREFIX);
  if (breadcrumbIndex >= 0) {
    return droppableId.slice(breadcrumbIndex + DROPPABLE_BREADCRUMB_PREFIX.length);
  }

  const folderIndex = droppableId.indexOf(DROPPABLE_FOLDER_PREFIX);
  if (folderIndex >= 0) {
    return droppableId.slice(folderIndex + DROPPABLE_FOLDER_PREFIX.length);
  }

  return undefined;
}

interface UseFileDragAndDropOptions {
  items: FileEntry[];
  searchQuery: string;
  isMoving: boolean;
  onMoveItems?: (itemIds: string[], targetFolderPath: string | undefined) => void;
}

export function useFileDragAndDrop({ items, searchQuery, isMoving, onMoveItems }: UseFileDragAndDropOptions) {
  const [activeItem, setActiveItem] = useState<FileEntry | undefined>(undefined);
  const [activeDragCount, setActiveDragCount] = useState(0);

  const isDragDisabled = isMoving || searchQuery.length > 0 || onMoveItems === undefined;

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: POINTER_ACTIVATION_DISTANCE_PX },
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: TOUCH_ACTIVATION_DELAY_MS,
      tolerance: TOUCH_ACTIVATION_TOLERANCE_PX,
    },
  });
  const sensors = useSensors(pointerSensor, touchSensor);

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const data = event.active.data.current;
      if (!isDragItemData(data)) return;

      const draggedItem = items.find((item) => item.id === data.itemId);
      setActiveItem(draggedItem);
      setActiveDragCount(data.selectedIds.length);
    },
    [items],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveItem(undefined);
      setActiveDragCount(0);

      if (onMoveItems === undefined) return;

      const { active, over } = event;
      if (over === null) return;

      const data = active.data.current;
      if (!isDragItemData(data)) return;

      const targetPath = resolveTargetPath(String(over.id));
      onMoveItems(data.selectedIds, targetPath);
    },
    [onMoveItems],
  );

  const handleDragCancel = useCallback(() => {
    setActiveItem(undefined);
    setActiveDragCount(0);
  }, []);

  return {
    sensors,
    collisionDetection: pointerWithin,
    activeItem,
    activeDragCount,
    isDragDisabled,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
  };
}
